// ============================================================================
// Column Mapping & Validation
// Maps parsed file columns to JobApplication fields with validation & dedup
// ============================================================================

import type { PipelineStage } from '../types'
import { applicationRepository } from '../storage/db'

// ---------------------------------------------------------------------------
// Field definitions - what can be mapped from imported data
// ---------------------------------------------------------------------------

export interface MappableField {
  key: string
  label: string
  required: boolean
  description: string
  example: string
  validate: (value: string) => string | null  // returns error or null
  transform?: (value: string) => string
}

export const MAPPABLE_FIELDS: MappableField[] = [
  {
    key: 'company',
    label: 'Company',
    required: true,
    description: 'Company or organization name',
    example: 'Google, Stripe, Airbnb',
    validate: (v) => v.trim() ? null : 'Company name is required'
  },
  {
    key: 'position',
    label: 'Position',
    required: true,
    description: 'Job title or role',
    example: 'Software Engineer, Product Manager',
    validate: (v) => v.trim() ? null : 'Position is required'
  },
  {
    key: 'appliedDate',
    label: 'Applied Date',
    required: false,
    description: 'Date the application was submitted',
    example: '2024-01-15, Jan 15 2024, 01/15/2024',
    validate: (v) => {
      if (!v.trim()) return null // optional
      const parsed = parseDate(v)
      return parsed ? null : 'Invalid date format'
    },
    transform: (v) => {
      if (!v.trim()) return new Date().toISOString().split('T')[0]
      const parsed = parseDate(v)
      return parsed || new Date().toISOString().split('T')[0]
    }
  },
  {
    key: 'stage',
    label: 'Stage',
    required: false,
    description: 'Current pipeline stage',
    example: 'applied, interviewing, offer, rejected, closed',
    validate: (v) => {
      if (!v.trim()) return null
      const normalized = normalizeStage(v)
      return normalized ? null : `Must be one of: applied, interviewing, offer, rejected, closed`
    },
    transform: (v) => normalizeStage(v) || 'applied'
  },
  {
    key: 'source',
    label: 'Source',
    required: false,
    description: 'Where the job was found',
    example: 'LinkedIn, Indeed, Referral',
    validate: () => null
  },
  {
    key: 'sourceUrl',
    label: 'Source URL',
    required: false,
    description: 'Link to the original job posting',
    example: 'https://linkedin.com/jobs/...',
    validate: (v) => {
      if (!v.trim()) return null
      try { new URL(v); return null } catch { return 'Invalid URL' }
    }
  },
  {
    key: 'notes',
    label: 'Notes',
    required: false,
    description: 'Any additional notes or comments',
    example: 'Referred by Jane Doe',
    validate: () => null
  },
  {
    key: 'followUpDate',
    label: 'Follow-up Date',
    required: false,
    description: 'Date for follow-up reminder',
    example: '2024-01-22',
    validate: (v) => {
      if (!v.trim()) return null
      const parsed = parseDate(v)
      return parsed ? null : 'Invalid date format'
    },
    transform: (v) => {
      if (!v.trim()) return ''
      const parsed = parseDate(v)
      return parsed || ''
    }
  },
]

// ---------------------------------------------------------------------------
// Column mapping type
// ---------------------------------------------------------------------------

export interface ColumnMapping {
  fileColumn: string   // header from the imported file
  appField: string     // key from MAPPABLE_FIELDS
}

// ---------------------------------------------------------------------------
// Validation result types
// ---------------------------------------------------------------------------

export interface RowValidation {
  rowIndex: number
  data: Record<string, string>
  mappedData: Record<string, string>
  isValid: boolean
  errors: string[]
  isDuplicate: boolean
  duplicateOf?: string  // existing application ID
}

export interface ImportPreview {
  totalRows: number
  validRows: number
  invalidRows: number
  duplicateCount: number
  newCount: number
  validations: RowValidation[]
  mappedFields: string[]
  missingRequired: string[]
}

// ---------------------------------------------------------------------------
// Auto-mapping heuristic
// ---------------------------------------------------------------------------

const HEADER_ALIASES: Record<string, string[]> = {
  company: ['company', 'company name', 'organization', 'employer', 'firm', 'business'],
  position: ['position', 'job title', 'title', 'role', 'job', 'job role', 'position title'],
  appliedDate: ['date applied', 'applied date', 'applied', 'date', 'applied at', 'application date', 'submission date', 'created at', 'date_submitted'],
  stage: ['stage', 'status', 'pipeline stage', 'application status', 'job status', 'pipeline'],
  source: ['source', 'where found', 'job board', 'platform', 'found on', 'site'],
  sourceUrl: ['url', 'link', 'source url', 'job url', 'job link', 'posting url', 'listing url'],
  notes: ['notes', 'note', 'comments', 'comment', 'description', 'details', 'remarks'],
  followUpDate: ['follow up', 'follow up date', 'follow-up', 'follow-up date', 'reminder date', 'next action date'],
}

/**
 * Auto-detect column mappings based on header names
 */
export function autoMapColumns(headers: string[]): ColumnMapping[] {
  const mappings: ColumnMapping[] = []
  const usedHeaders = new Set<number>()

  for (const [fieldKey, aliases] of Object.entries(HEADER_ALIASES)) {
    for (let i = 0; i < headers.length; i++) {
      if (usedHeaders.has(i)) continue
      const headerLower = headers[i].toLowerCase().trim()
      if (aliases.some(alias => headerLower === alias || headerLower.includes(alias))) {
        mappings.push({ fileColumn: headers[i], appField: fieldKey })
        usedHeaders.add(i)
        break
      }
    }
  }

  return mappings
}

// ---------------------------------------------------------------------------
// Validation & preview
// ---------------------------------------------------------------------------

/**
 * Build an import preview with validation and duplicate detection
 */
export async function buildImportPreview(
  rows: Record<string, string>[],
  mappings: ColumnMapping[]
): Promise<ImportPreview> {
  const mappedFields = new Set(mappings.map(m => m.appField))
  const requiredFields = MAPPABLE_FIELDS.filter(f => f.required).map(f => f.key)
  const missingRequired = requiredFields.filter(f => !mappedFields.has(f))

  // Load existing applications for dedup
  const existingApps = await applicationRepository.getAll()

  const validations: RowValidation[] = rows.map((row, index) => {
    const mappedData = applyMappings(row, mappings)
    const errors: string[] = []

    // Validate each mapped field
    for (const mapping of mappings) {
      const field = MAPPABLE_FIELDS.find(f => f.key === mapping.appField)
      if (!field) continue

      const rawValue = row[mapping.fileColumn] || ''
      const error = field.validate(rawValue)
      if (error) {
        errors.push(`${field.label}: ${error}`)
      }
    }

    // Check required fields have values
    for (const reqField of requiredFields) {
      const mapping = mappings.find(m => m.appField === reqField)
      if (mapping) {
        const value = (row[mapping.fileColumn] || '').trim()
        if (!value) {
          const field = MAPPABLE_FIELDS.find(f => f.key === reqField)
          errors.push(`${field?.label || reqField} is required`)
        }
      }
    }

    // Check for duplicates (company + position + date)
    const company = mappedData.company?.trim().toLowerCase() || ''
    const position = mappedData.position?.trim().toLowerCase() || ''
    const appliedDate = mappedData.appliedDate || ''

    const duplicate = existingApps.find(app => {
      const matchCompany = app.company.toLowerCase() === company
      const matchPosition = app.position.toLowerCase() === position
      const matchDate = !appliedDate || app.appliedDate === appliedDate
      return matchCompany && matchPosition && matchDate
    })

    const isValid = errors.length === 0

    return {
      rowIndex: index,
      data: row,
      mappedData,
      isValid,
      errors,
      isDuplicate: !!duplicate,
      duplicateOf: duplicate?.id,
    }
  })

  const validRows = validations.filter(v => v.isValid).length
  const invalidRows = validations.filter(v => !v.isValid).length
  const duplicateCount = validations.filter(v => v.isDuplicate).length
  const newCount = validations.filter(v => v.isValid && !v.isDuplicate).length

  return {
    totalRows: rows.length,
    validRows,
    invalidRows,
    duplicateCount,
    newCount,
    validations,
    mappedFields: Array.from(mappedFields),
    missingRequired,
  }
}

/**
 * Apply column mappings to a row of data, producing mapped application fields
 */
function applyMappings(row: Record<string, string>, mappings: ColumnMapping[]): Record<string, string> {
  const result: Record<string, string> = {}

  for (const mapping of mappings) {
    const rawValue = row[mapping.fileColumn] || ''
    const field = MAPPABLE_FIELDS.find(f => f.key === mapping.appField)

    if (field?.transform) {
      result[mapping.appField] = field.transform(rawValue)
    } else {
      result[mapping.appField] = rawValue.trim()
    }
  }

  return result
}

// ---------------------------------------------------------------------------
// Date parsing utility
// ---------------------------------------------------------------------------

function parseDate(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null

  // Try ISO format first (YYYY-MM-DD)
  const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (isoMatch) {
    const [, y, m, d] = isoMatch
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
  }

  // Try MM/DD/YYYY or DD/MM/YYYY (assume MM/DD for US)
  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (slashMatch) {
    const [, m, d, y] = slashMatch
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
  }

  // Try MM-DD-YYYY
  const dashMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/)
  if (dashMatch) {
    const [, m, d, y] = dashMatch
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
  }

  // Try natural language via Date constructor
  const parsed = new Date(trimmed)
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString().split('T')[0]
  }

  return null
}

// ---------------------------------------------------------------------------
// Stage normalization
// ---------------------------------------------------------------------------

function normalizeStage(value: string): PipelineStage | null {
  const lower = value.toLowerCase().trim()

  const stageMap: Record<string, PipelineStage> = {
    'applied': 'applied',
    'submitted': 'applied',
    'sent': 'applied',
    'interviewing': 'interviewing',
    'interview': 'interviewing',
    'in progress': 'interviewing',
    'in-progress': 'interviewing',
    'phone screen': 'interviewing',
    'phone interview': 'interviewing',
    'onsite': 'interviewing',
    'offer': 'offer',
    'offered': 'offer',
    'accepted': 'offer',
    'hired': 'offer',
    'rejected': 'rejected',
    'declined': 'rejected',
    'denied': 'rejected',
    'not selected': 'rejected',
    'no offer': 'rejected',
    'closed': 'closed',
    'withdrawn': 'closed',
    'withdrew': 'closed',
    'withdraw': 'closed',
  }

  // Direct match
  if (stageMap[lower]) return stageMap[lower]

  // Partial match
  for (const [key, stage] of Object.entries(stageMap)) {
    if (lower.includes(key) || key.includes(lower)) return stage
  }

  return null
}
